// Common frame for every daily-cycle action panel: why the step is unavailable, a
// version change that must be acknowledged, what is missing, the server's answer
// (blockers with their rule ids) and the sticky Cancel / Sign bar.
import type { ReactNode } from 'react'
import { ErrorBanner } from '@/components/evidence'
import { Banner, Button } from '@/components/ui'
import { eventKindLabel } from '@/domain/catalogue'
import type { AvailableAction } from '@/domain/types'
import { fmtDateTime } from '@/lib/format'
import type { PanelCommandState } from './hooks'
import { ActionUnavailable, FormProblems, PanelFooter } from './parts'

function VersionChanged({ changes, onAcknowledge }: { changes: PanelCommandState['changes']; onAcknowledge: () => void }) {
  return (
    <Banner tone="warn" title="The permit changed while this form was open" className="mb-4">
      {changes.length > 0 ? (
        <ul className="mb-2 list-disc space-y-1 pl-5">
          {changes.map((e) => (
            <li key={e.sequence}>
              {eventKindLabel(e.event_kind)}
              {e.actor ? ` by ${e.actor.display_name}` : ''}, {fmtDateTime(e.server_recorded_at)}
            </li>
          ))}
        </ul>
      ) : null}
      <p>This form now shows the latest record. Read it again, then confirm to continue.</p>
      <Button type="button" variant="secondary" className="mt-2" onClick={onAcknowledge}>
        I have checked the changes
      </Button>
    </Banner>
  )
}

export function PanelFrame({ action, run, problems, tried, onCancel, onSubmit, submitLabel, danger, blockedNote, children }: {
  action: AvailableAction
  run: PanelCommandState
  /** Missing or malformed input; shown after the first attempt to send. */
  problems: string[]
  tried: boolean
  onCancel: () => void
  onSubmit: () => void
  submitLabel: string
  danger?: boolean
  /** Set when the panel cannot send anything at all (for example the cycle is missing). */
  blockedNote?: string | null
  children: ReactNode
}) {
  const disabledNote =
    blockedNote ??
    (!action.enabled ? 'Not available now: see the message at the top.' : run.changed ? 'Confirm that you have checked the changes first.' : null)
  return (
    <div>
      <ActionUnavailable action={action} />
      {run.changed ? <VersionChanged changes={run.changes} onAcknowledge={run.acknowledgeChanges} /> : null}
      {children}
      {tried ? <FormProblems problems={problems} /> : null}
      {run.error ? (
        <div className="mb-4">
          {/* The kit's default title repeats the message for network failures; name the outcome instead. */}
          <ErrorBanner error={run.error} title={run.error.code === 'NETWORK' ? 'Not confirmed by the server' : undefined} />
        </div>
      ) : null}
      <PanelFooter
        onCancel={onCancel}
        onSubmit={onSubmit}
        submitLabel={submitLabel}
        pending={run.pending}
        disabled={Boolean(disabledNote)}
        danger={danger}
        note={disabledNote}
      />
    </div>
  )
}
